// ABG Logic based on Boston approach
// This file contains the core logic for ABG interpretation

// Normal ranges
const NORMAL_RANGES = {
  pH: { min: 7.35, max: 7.45 },
  paCO2: { min: 35, max: 45 }, // mmHg
  hco3: { min: 22, max: 26 }, // mEq/L
  anionGap: { min: 8, max: 12 }, // mEq/L
  sodium: { min: 135, max: 145 }, // mEq/L
  chloride: { min: 98, max: 108 }, // mEq/L
  albumin: { min: 3.5, max: 5.0 }, // g/dL
};

// Boston compensation rules
export const calculateExpectedHCO3 = (paCO2, isAcute = true) => {
  // For respiratory acidosis
  if (paCO2 > NORMAL_RANGES.paCO2.max) {
    if (isAcute) {
      // Acute: For every 10 mmHg increase in PaCO2, HCO3- rises by 1 mmol/L
      return 24 + (paCO2 - 40) / 10;
    } else {
      // Chronic: For every 10 mmHg increase in PaCO2, HCO3- rises by 4 mmol/L
      return 24 + (4 * (paCO2 - 40)) / 10;
    }
  }
  // For respiratory alkalosis
  else if (paCO2 < NORMAL_RANGES.paCO2.min) {
    if (isAcute) {
      // Acute: For every 10 mmHg decrease in PaCO2, HCO3- falls by 2 mmol/L
      return 24 - (2 * (40 - paCO2)) / 10;
    } else {
      // Chronic: For every 10 mmHg decrease in PaCO2, HCO3- falls by 5 mmol/L
      return 24 - (5 * (40 - paCO2)) / 10;
    }
  }
  // Normal paCO2
  return 24;
};

export const calculateExpectedPaCO2 = (hco3) => {
  // For metabolic acidosis
  if (hco3 < NORMAL_RANGES.hco3.min) {
    // Expected PaCO2 = (1.5 × HCO3-) + 8
    return 1.5 * hco3 + 8;
  }
  // For metabolic alkalosis
  else if (hco3 > NORMAL_RANGES.hco3.max) {
    // Expected PaCO2 = (0.7 × HCO3-) + 20
    return 0.7 * hco3 + 20;
  }
  // Normal HCO3
  return 40;
};

// Anion gap calculation
export const calculateAnionGap = (sodium, chloride, hco3) => {
  // Anion Gap = Na+ - (Cl- + HCO3-)
  return sodium - (chloride + hco3);
};

// Albumin-corrected anion gap calculation
export const calculateCorrectedAnionGap = (anionGap, albumin) => {
  // Corrected AG = Calculated AG + 2.5 × (4 - measured albumin)
  return anionGap + 2.5 * (4 - albumin);
};

// Delta gap calculation
export const calculateDeltaGap = (anionGap, hco3) => {
  // Delta Gap = (AG - 12) / (24 - HCO3-)
  return (anionGap - 12) / (24 - hco3);
};

// Interpret anion gap
export const interpretAnionGap = (anionGap, isAlbuminCorrected = false) => {
  const range = isAlbuminCorrected
    ? { min: 8, max: 16 } // Wider range for albumin-corrected AG
    : NORMAL_RANGES.anionGap;

  if (anionGap < range.min) {
    return "Low";
  } else if (anionGap > range.max) {
    return "Elevated";
  } else {
    return "Normal";
  }
};

// Interpret delta gap
export const interpretDeltaGap = (deltaGap) => {
  if (deltaGap < 0.4) {
    return "Combined anion gap and non-anion gap metabolic acidosis";
  } else if (deltaGap <= 0.8) {
    return "Pure anion gap metabolic acidosis";
  } else {
    return "Combined anion gap metabolic acidosis and metabolic alkalosis";
  }
};

// Main analysis function
export const analyzeABG = (values, isAcute = true) => {
  const { pH, paCO2, hco3, sodium, chloride, albumin } = values;
  const steps = [];
  let primaryDisorder = "";
  let compensation = "";
  let isCompensated = false;
  let isMixed = false;
  let finalDiagnosis = "";

  // Step 1: Evaluate pH
  steps.push({
    title: "Step 1: Evaluate pH",
    description: "Determine if acidemia or alkalemia is present",
    result:
      pH < NORMAL_RANGES.pH.min
        ? "Acidemia"
        : pH > NORMAL_RANGES.pH.max
        ? "Alkalemia"
        : "Normal pH",
    isNormal: pH >= NORMAL_RANGES.pH.min && pH <= NORMAL_RANGES.pH.max,
    details: `pH is ${pH.toFixed(2)}. Normal range is ${NORMAL_RANGES.pH.min}-${
      NORMAL_RANGES.pH.max
    }.`,
  });

  // Step 2: Identify primary disorder
  let step2Result = "";
  let step2IsNormal = true;
  let step2Details = "";

  if (pH < NORMAL_RANGES.pH.min) {
    // Acidemia
    if (paCO2 > NORMAL_RANGES.paCO2.max) {
      primaryDisorder = "Respiratory Acidosis";
      step2Result = "Respiratory Acidosis";
      step2IsNormal = false;
      step2Details = `PaCO2 is elevated at ${paCO2.toFixed(1)} mmHg (normal: ${
        NORMAL_RANGES.paCO2.min
      }-${NORMAL_RANGES.paCO2.max} mmHg).`;
    } else if (hco3 < NORMAL_RANGES.hco3.min) {
      primaryDisorder = "Metabolic Acidosis";
      step2Result = "Metabolic Acidosis";
      step2IsNormal = false;
      step2Details = `HCO3- is decreased at ${hco3.toFixed(1)} mEq/L (normal: ${
        NORMAL_RANGES.hco3.min
      }-${NORMAL_RANGES.hco3.max} mEq/L).`;
    } else {
      step2Result = "Mixed Disorder";
      step2IsNormal = false;
      step2Details =
        "Acidemia is present but neither PaCO2 nor HCO3- clearly indicates a primary disorder.";
      isMixed = true;
    }
  } else if (pH > NORMAL_RANGES.pH.max) {
    // Alkalemia
    if (paCO2 < NORMAL_RANGES.paCO2.min) {
      primaryDisorder = "Respiratory Alkalosis";
      step2Result = "Respiratory Alkalosis";
      step2IsNormal = false;
      step2Details = `PaCO2 is decreased at ${paCO2.toFixed(1)} mmHg (normal: ${
        NORMAL_RANGES.paCO2.min
      }-${NORMAL_RANGES.paCO2.max} mmHg).`;
    } else if (hco3 > NORMAL_RANGES.hco3.max) {
      primaryDisorder = "Metabolic Alkalosis";
      step2Result = "Metabolic Alkalosis";
      step2IsNormal = false;
      step2Details = `HCO3- is elevated at ${hco3.toFixed(1)} mEq/L (normal: ${
        NORMAL_RANGES.hco3.min
      }-${NORMAL_RANGES.hco3.max} mEq/L).`;
    } else {
      step2Result = "Mixed Disorder";
      step2IsNormal = false;
      step2Details =
        "Alkalemia is present but neither PaCO2 nor HCO3- clearly indicates a primary disorder.";
      isMixed = true;
    }
  } else {
    // Normal pH
    if (paCO2 < NORMAL_RANGES.paCO2.min && hco3 < NORMAL_RANGES.hco3.min) {
      step2Result =
        "Mixed Disorder (Respiratory Alkalosis + Metabolic Acidosis)";
      step2IsNormal = false;
      step2Details =
        "Normal pH with both low PaCO2 and low HCO3- suggests a mixed disorder.";
      isMixed = true;
    } else if (
      paCO2 > NORMAL_RANGES.paCO2.max &&
      hco3 > NORMAL_RANGES.hco3.max
    ) {
      step2Result =
        "Mixed Disorder (Respiratory Acidosis + Metabolic Alkalosis)";
      step2IsNormal = false;
      step2Details =
        "Normal pH with both high PaCO2 and high HCO3- suggests a mixed disorder.";
      isMixed = true;
    } else if (
      paCO2 > NORMAL_RANGES.paCO2.max ||
      paCO2 < NORMAL_RANGES.paCO2.min ||
      hco3 > NORMAL_RANGES.hco3.max ||
      hco3 < NORMAL_RANGES.hco3.min
    ) {
      step2Result = "Fully Compensated Disorder";
      step2IsNormal = false;
      step2Details =
        "Normal pH with abnormal PaCO2 or HCO3- suggests a fully compensated disorder.";
      isCompensated = true;
    } else {
      step2Result = "Normal ABG Values";
      step2IsNormal = true;
      step2Details = "All parameters are within normal ranges.";
    }
  }

  steps.push({
    title: "Step 2: Identify Primary Disorder",
    description: "Determine the primary acid-base disturbance",
    result: step2Result,
    isNormal: step2IsNormal,
    details: step2Details,
  });

  // Step 3: Assess compensation
  if (primaryDisorder && !isMixed) {
    let expectedValue = 0;
    let actualValue = 0;
    let compensationFormula = "";
    let compensationDetails = "";

    if (
      primaryDisorder === "Respiratory Acidosis" ||
      primaryDisorder === "Respiratory Alkalosis"
    ) {
      expectedValue = calculateExpectedHCO3(paCO2, isAcute);
      actualValue = hco3;

      if (primaryDisorder === "Respiratory Acidosis") {
        compensationFormula = isAcute
          ? "Expected HCO3- = 24 + ((PaCO2-40) / 10)"
          : "Expected HCO3- = 24 + (4 × (PaCO2-40) / 10)";
      } else {
        compensationFormula = isAcute
          ? "Expected HCO3- = 24 - (2 × (40-PaCO2) / 10)"
          : "Expected HCO3- = 24 - (5 × (40-PaCO2) / 10)";
      }

      compensationDetails = `Expected HCO3-: ${expectedValue.toFixed(
        1
      )} mEq/L, Actual HCO3-: ${actualValue.toFixed(1)} mEq/L`;

      // Check if there's appropriate compensation
      const difference = Math.abs(actualValue - expectedValue);
      if (difference <= 2) {
        compensation = isAcute
          ? "Appropriate Acute Compensation"
          : "Appropriate Chronic Compensation";
        isCompensated = true;
      } else if (actualValue > expectedValue + 2) {
        compensation = "Additional Metabolic Alkalosis";
        isMixed = true;
      } else if (actualValue < expectedValue - 2) {
        compensation = "Additional Metabolic Acidosis";
        isMixed = true;
      }
    } else if (
      primaryDisorder === "Metabolic Acidosis" ||
      primaryDisorder === "Metabolic Alkalosis"
    ) {
      expectedValue = calculateExpectedPaCO2(hco3);
      actualValue = paCO2;

      if (primaryDisorder === "Metabolic Acidosis") {
        compensationFormula = "Expected PaCO2 = (1.5 × HCO3-) + 8";
      } else {
        compensationFormula = "Expected PaCO2 = (0.7 × HCO3-) + 20";
      }

      compensationDetails = `Expected PaCO2: ${expectedValue.toFixed(
        1
      )} mmHg, Actual PaCO2: ${actualValue.toFixed(1)} mmHg`;

      // Check if there's appropriate compensation
      const difference = Math.abs(actualValue - expectedValue);
      if (difference <= 2) {
        compensation = "Appropriate Respiratory Compensation";
        isCompensated = true;
      } else if (actualValue > expectedValue + 2) {
        compensation = "Additional Respiratory Acidosis";
        isMixed = true;
      } else if (actualValue < expectedValue - 2) {
        compensation = "Additional Respiratory Alkalosis";
        isMixed = true;
      }
    }

    steps.push({
      title: "Step 3: Assess Compensation",
      description: "Determine if compensation is appropriate",
      calculation: compensationFormula,
      result: compensation || "No compensation detected",
      isNormal: isCompensated,
      details: compensationDetails,
    });
  } else if (isMixed) {
    steps.push({
      title: "Step 3: Assess Compensation",
      description: "Determine if compensation is appropriate",
      result:
        "Mixed disorder detected - compensation assessment not applicable",
      isNormal: false,
      details:
        "When a mixed disorder is present, standard compensation rules do not apply.",
    });
  }

  // Step 4: Anion Gap Assessment (if sodium and chloride are provided)
  if (sodium && chloride) {
    const anionGap = calculateAnionGap(sodium, chloride, hco3);
    let anionGapDetails = `Anion Gap = Na⁺ - (Cl⁻ + HCO₃⁻) = ${sodium} - (${chloride} + ${hco3.toFixed(
      1
    )}) = ${anionGap.toFixed(1)} mEq/L`;
    let anionGapResult = `Anion Gap: ${anionGap.toFixed(
      1
    )} mEq/L - ${interpretAnionGap(anionGap)}`;
    let isNormalAG =
      anionGap >= NORMAL_RANGES.anionGap.min &&
      anionGap <= NORMAL_RANGES.anionGap.max;

    // If albumin is provided and not 4.0, calculate corrected anion gap
    if (albumin && Math.abs(albumin - 4.0) > 0.1) {
      const correctedAG = calculateCorrectedAnionGap(anionGap, albumin);
      anionGapDetails += `\nAlbumin-Corrected Anion Gap = ${anionGap.toFixed(
        1
      )} + 2.5 × (4 - ${albumin}) = ${correctedAG.toFixed(1)} mEq/L`;
      anionGapResult += `\nAlbumin-Corrected Anion Gap: ${correctedAG.toFixed(
        1
      )} mEq/L - ${interpretAnionGap(correctedAG, true)}`;
      isNormalAG = isNormalAG || (correctedAG >= 8 && correctedAG <= 16);
    }

    // If anion gap is elevated and metabolic acidosis is present, calculate delta gap
    if (
      anionGap > NORMAL_RANGES.anionGap.max &&
      (primaryDisorder === "Metabolic Acidosis" ||
        compensation === "Additional Metabolic Acidosis" ||
        step2Result.includes("Metabolic Acidosis"))
    ) {
      const deltaGap = calculateDeltaGap(anionGap, hco3);
      const deltaInterpretation = interpretDeltaGap(deltaGap);

      anionGapDetails += `\nDelta Gap = (AG - 12) / (24 - HCO₃⁻) = (${anionGap.toFixed(
        1
      )} - 12) / (24 - ${hco3.toFixed(1)}) = ${deltaGap.toFixed(2)}`;
      anionGapResult += `\nDelta Gap: ${deltaGap.toFixed(
        2
      )} - ${deltaInterpretation}`;
    }

    steps.push({
      title: "Step 4: Anion Gap Assessment",
      description: "Evaluate anion gap to identify type of metabolic acidosis",
      calculation: "Anion Gap = Na⁺ - (Cl⁻ + HCO₃⁻)",
      result: anionGapResult,
      isNormal: isNormalAG,
      details: anionGapDetails,
    });
  }

  // Final Diagnosis (now Step 5 if anion gap was calculated)
  if (isMixed) {
    if (step2Result.includes("Mixed Disorder")) {
      finalDiagnosis = step2Result;
    } else {
      finalDiagnosis = `${primaryDisorder} with ${compensation}`;
    }
  } else if (isCompensated) {
    if (pH >= NORMAL_RANGES.pH.min && pH <= NORMAL_RANGES.pH.max) {
      finalDiagnosis = `Fully Compensated ${primaryDisorder}`;
    } else {
      finalDiagnosis = `Partially Compensated ${primaryDisorder}`;
    }
  } else {
    finalDiagnosis = primaryDisorder || "Normal ABG Values";
  }

  // Add anion gap information to final diagnosis if relevant
  if (sodium && chloride) {
    const anionGap = calculateAnionGap(sodium, chloride, hco3);
    if (
      anionGap > NORMAL_RANGES.anionGap.max &&
      finalDiagnosis.includes("Metabolic Acidosis")
    ) {
      finalDiagnosis = finalDiagnosis.replace(
        "Metabolic Acidosis",
        "Anion Gap Metabolic Acidosis"
      );

      // Add delta gap interpretation if applicable
      const deltaGap = calculateDeltaGap(anionGap, hco3);
      if (deltaGap < 0.4) {
        finalDiagnosis += " with concurrent Non-Anion Gap Metabolic Acidosis";
      } else if (deltaGap > 0.8) {
        finalDiagnosis += " with concurrent Metabolic Alkalosis";
      }
    } else if (
      anionGap < NORMAL_RANGES.anionGap.min &&
      finalDiagnosis.includes("Metabolic Acidosis")
    ) {
      finalDiagnosis = finalDiagnosis.replace(
        "Metabolic Acidosis",
        "Non-Anion Gap Metabolic Acidosis"
      );
    }
  }

  steps.push({
    title:
      sodium && chloride
        ? "Step 5: Final Diagnosis"
        : "Step 4: Final Diagnosis",
    description: "Comprehensive assessment of acid-base status",
    result: finalDiagnosis,
    isNormal: finalDiagnosis === "Normal ABG Values",
    details: `pH: ${pH.toFixed(2)}, PaCO2: ${paCO2.toFixed(
      1
    )} mmHg, HCO3-: ${hco3.toFixed(1)} mEq/L${
      sodium && chloride ? `, Na⁺: ${sodium} mEq/L, Cl⁻: ${chloride} mEq/L` : ""
    }${albumin ? `, Albumin: ${albumin} g/dL` : ""}`,
  });

  return {
    steps,
    primaryDisorder,
    compensation,
    isCompensated,
    isMixed,
    finalDiagnosis,
  };
};

// Helper function to get clinical examples
export const getClinicalExamples = () => {
  return [
    {
      name: "Normal ABG",
      values: { pH: 7.4, paCO2: 40, hco3: 24, sodium: 140, chloride: 104 },
    },
    {
      name: "Acute Respiratory Acidosis",
      values: { pH: 7.25, paCO2: 60, hco3: 26, sodium: 138, chloride: 102 },
    },
    {
      name: "Chronic Respiratory Acidosis",
      values: { pH: 7.36, paCO2: 60, hco3: 32, sodium: 139, chloride: 100 },
    },
    {
      name: "Acute Respiratory Alkalosis",
      values: { pH: 7.5, paCO2: 25, hco3: 22, sodium: 141, chloride: 105 },
    },
    {
      name: "Chronic Respiratory Alkalosis",
      values: { pH: 7.42, paCO2: 30, hco3: 19, sodium: 140, chloride: 106 },
    },
    {
      name: "Metabolic Acidosis with Compensation",
      values: { pH: 7.3, paCO2: 30, hco3: 15, sodium: 140, chloride: 110 },
    },
    {
      name: "Anion Gap Metabolic Acidosis (DKA)",
      values: { pH: 7.24, paCO2: 28, hco3: 12, sodium: 138, chloride: 100 },
    },
    {
      name: "Metabolic Alkalosis with Compensation",
      values: { pH: 7.48, paCO2: 45, hco3: 32, sodium: 142, chloride: 90 },
    },
    {
      name: "Mixed Respiratory and Metabolic Acidosis",
      values: { pH: 7.15, paCO2: 50, hco3: 16, sodium: 139, chloride: 100 },
    },
    {
      name: "Mixed Respiratory and Metabolic Alkalosis",
      values: { pH: 7.55, paCO2: 30, hco3: 30, sodium: 143, chloride: 95 },
    },
    {
      name: "Triple Disorder (HAGMA + NAGMA)",
      values: { pH: 7.22, paCO2: 32, hco3: 13, sodium: 140, chloride: 112 },
    },
  ];
};
